// After Dark -- the show reaches over and turns the lights out on every card at
// once, and turns them back up if the call is taken back.
//
// HOW THE LOOKS ON THIS SITE ACTUALLY STACK. Every theme is a whole stylesheet
// (see the switch in App.js), but the everyday one is NOT swapped out for the
// chosen one: BingoCard.css is pulled in unconditionally by a static import in
// components/whenplane/WhenPlane.js, and ?theme=bread simply lands another
// stylesheet on top of it. Cascade order is what decides the look, and it always
// has been.
//
// So this does exactly what ?theme=afterdark does and nothing more: it puts
// BingoCard-AFTERDARK.css last. Undoing it switches that one sheet off, which
// leaves the page byte-for-byte in the state it was in beforehand -- whatever
// theme the visitor had chosen comes straight back, with nothing to restore and
// nothing that can be left half-applied.
//
// The swap itself is instant and cannot be tweened (there is no animating
// between two stylesheets), so the change is hidden inside a fade: a veil closes
// over the page, the sheet changes behind it, and it opens again on the new
// look. The veil is a fixed, pointer-events:none layer appended to <body>, so it
// shifts no layout and swallows no clicks, and every one of its styles is inline
// so no theme -- present or future -- can accidentally restyle it.
//
// prefers-reduced-motion skips the veil entirely and lands on the end state at
// once, and so does a page that is not on screen (a hidden tab throttles timers
// and there is nobody watching anyway).

const FADE_IN_MS = 260;    // the lights close in
const HOLD_MS = 90;        // ...and the stylesheet changes behind them
const FADE_OUT_MS = 520;   // ...and they open again on the new look
                           // 870ms all told: deliberate, not a flicker

let baseLoaded = false;
let darkNodes = null;      // the <style> element(s) of the After Dark stylesheet
let darkLoaded = false;
let applied = false;
let running = null;        // { veil, timers } while a fade is in flight

function styleElements() {
  return Array.prototype.slice.call(
    document.querySelectorAll('style, link[rel="stylesheet"]')
  );
}

/** Whatever a require() of a stylesheet just added to the document. */
function addedSince(before) {
  return styleElements().filter((node) => before.indexOf(node) === -1);
}

function setDisabled(nodes, disabled) {
  if (!nodes) return;
  nodes.forEach((node) => {
    // Both spellings: the element property is the supported one, the sheet
    // property is belt and braces for anything that disagrees.
    try { node.disabled = disabled; } catch (e) { /* ignore */ }
    try { if (node.sheet) node.sheet.disabled = disabled; } catch (e) { /* ignore */ }
  });
}

function reducedMotion() {
  try {
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
}

/**
 * Run the app's own theme loader, once.
 *
 * Its real job is ordering: the visitor's theme has to be in the document
 * before After Dark is ever asked for, so that when it is, it lands after it.
 * App calls this from its mount effect and the lights can only be called for
 * after that, so the order is guaranteed.
 */
export function registerBaseTheme(load) {
  if (baseLoaded) return;                // StrictMode runs mount effects twice
  baseLoaded = true;
  load();
}

function loadDarkTheme() {
  if (darkLoaded) return;
  darkLoaded = true;
  const before = styleElements();
  // A static string, so webpack has already bundled it; requiring it here just
  // decides WHEN it is injected -- the first time a show goes dark, rather than
  // on every page load for everybody.
  require('./assets/css/BingoCard-AFTERDARK.css');
  darkNodes = addedSince(before);
}

function swapSheets(on) {
  if (on) loadDarkTheme();
  // Nothing to switch means nothing to do -- never leave the page part-themed.
  if (!darkNodes || !darkNodes.length) return;
  setDisabled(darkNodes, !on);
}

function clearRunning(removeVeil) {
  if (!running) return;
  running.timers.forEach(clearTimeout);
  if (removeVeil && running.veil && running.veil.parentNode) {
    running.veil.parentNode.removeChild(running.veil);
  }
  running = null;
}

function buildVeil() {
  const veil = document.createElement('div');
  veil.setAttribute('aria-hidden', 'true');
  veil.style.cssText = [
    'position:fixed',
    'top:0', 'right:0', 'bottom:0', 'left:0',
    'margin:0', 'padding:0', 'border:0',
    'z-index:2147483000',
    'pointer-events:none',            // never blocks a click on the card
    'opacity:0',
    'will-change:opacity',
    'background:radial-gradient(125% 95% at 50% 38%,'
      + ' rgba(14,7,48,0.94) 0%, rgba(4,1,16,0.985) 55%, rgb(0,0,0) 100%)',
    'transition:opacity ' + FADE_IN_MS + 'ms cubic-bezier(0.4,0,0.2,1)',
  ].join(';');

  // A soft band of light that drifts down through the dark and settles as the
  // veil opens. Nothing but opacity and transform, so it stays on the compositor.
  const sheen = document.createElement('div');
  sheen.style.cssText = [
    'position:absolute',
    'top:0', 'right:0', 'bottom:0', 'left:0',
    'margin:0', 'padding:0', 'border:0',
    'opacity:0',
    'pointer-events:none',
    'will-change:opacity,transform',
    'background:radial-gradient(58% 42% at 50% 30%,'
      + ' rgba(255,255,255,0.17) 0%, rgba(255,255,255,0) 72%)',
    'transform:translate3d(0,-14%,0) scale(1.09)',
    'transition:opacity ' + FADE_IN_MS + 'ms ease,'
      + ' transform ' + FADE_IN_MS + 'ms cubic-bezier(0.4,0,0.2,1)',
  ].join(';');

  veil.appendChild(sheen);
  return { veil, sheen };
}

/**
 * Put After Dark on, or take it off.
 *
 * @param {boolean} on
 * @param {{animate?: boolean, onSwap?: function}} [options]
 *        animate  false lands on the end state instantly -- used for the first
 *                 answer about tonight, so somebody opening the page mid-show
 *                 ARRIVES dark rather than watching it happen to them.
 *        onSwap   called at the exact moment the stylesheet changes, i.e. while
 *                 the veil is closed, so anything React swaps at the same time
 *                 (the logo) changes with everything else and not a beat early.
 */
export function applyAfterDark(on, options) {
  const want = !!on;
  const opts = options || {};
  const finish = () => { if (typeof opts.onSwap === 'function') opts.onSwap(); };

  if (applied === want) return;
  applied = want;

  const instant = !opts.animate
    || reducedMotion()
    || (typeof document !== 'undefined' && document.hidden);

  clearRunning(true);

  if (instant) {
    swapSheets(want);
    finish();
    return;
  }

  const { veil, sheen } = buildVeil();
  document.body.appendChild(veil);
  // Make the browser accept the starting values before changing them; without
  // this both are coalesced into one style recalculation and nothing moves.
  /* eslint-disable-next-line no-unused-expressions */
  veil.offsetHeight;

  veil.style.opacity = '1';
  sheen.style.opacity = '0.9';
  sheen.style.transform = 'translate3d(0,0,0) scale(1)';

  const timers = [];
  running = { veil, timers };

  timers.push(setTimeout(() => {
    swapSheets(want);
    finish();

    veil.style.transition = 'opacity ' + FADE_OUT_MS + 'ms cubic-bezier(0.16,1,0.3,1)';
    sheen.style.transition = 'opacity ' + FADE_OUT_MS + 'ms ease,'
      + ' transform ' + FADE_OUT_MS + 'ms cubic-bezier(0.16,1,0.3,1)';
    veil.style.opacity = '0';
    sheen.style.opacity = '0';
    sheen.style.transform = 'translate3d(0,7%,0) scale(1.03)';

    timers.push(setTimeout(() => clearRunning(true), FADE_OUT_MS + 60));
  }, FADE_IN_MS + HOLD_MS));
}

/** Whether the After Dark stylesheet is currently the one in charge. */
export function isAfterDarkApplied() {
  return applied;
}
